import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    DataGrid,
    GridToolbarQuickFilter,
    GridToolbarContainer
} from '@mui/x-data-grid';

import DatabaseService from "../../services/database.service.js";
import { convertDateToString, convertDurationToString } from '../../common/ConvertTools.js';
import { getBootstrapColors } from '../race/RaceColors.js';

import gridSxStyle from "../../common/GridSxStyles.js";

// Custom comparison function
const areEqual = (prevProps, nextProps) => {
    const isEqual = JSON.stringify(prevProps) === JSON.stringify(nextProps);

    if (!isEqual) {
        console.log('PerfsGrid re-rendering because props changed:', {
            prevProps,
            nextProps,
            differences: {
                contentRun: prevProps.contentRun !== nextProps.contentRun,
                setContentRun: prevProps.setContentRun !== nextProps.setContentRun,
                isDesktop: prevProps.isDesktop !== nextProps.isDesktop,
                loadData: prevProps.loadData !== nextProps.loadData,
                removeData: prevProps.removeData !== nextProps.removeData,
            }
        });
    }

    return isEqual;
};

const PerfsGrid = React.memo(({ contentRun, setContentRun, isDesktop, loadData, removeData }) => {

    console.log("PerfsGrid / refresh");

    const DEFAULT_PAGINATION_MODEL = { pageSize: isDesktop ? 14 : 10 }
    const DEFAULT_FILTER_MODEL = { filterModel: { items: [] } };
    const [selectRun, setSelectRun] = useState({});
    const [devicesPaginationModel, setDevicesPaginationModel] = useState(DEFAULT_PAGINATION_MODEL);
    const [devicesFilterModel, setDevicesFilterModel] = useState(DEFAULT_FILTER_MODEL);

    const columns = [
        {
            field: 'actions', headerName: '',
            width: "85",
            resizable: false,
            renderCell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>
                    <i className={`fa ${((row?.laps != null) && (row?.laps.length > 0)) ? "fa-forward" : "fa-play"}`}
                        style={{
                            color: row.timestamp === selectRun ? getBootstrapColors()[0] : "",
                            // width: 20,
                        }}
                        onClick={() => {
                            setSelectRun(row.timestamp);
                            loadData(row.timestamp);
                        }}
                    />
                    <i className="fa fa-trash" style={{ paddingLeft: "15px" }} onClick={() => { removeData(row.timestamp, row.device_id); }}></i>
                    <i className={`fa ${row.lock ? "fa-lock" : "fa-lock-open"}`}
                        style={{
                            paddingLeft: "15px",
                            color: row.lock ? getBootstrapColors()[0] : ""
                        }}
                        onClick={() => {
                            DatabaseService.lockPerfsRun(row.id, !row.lock).then((response) => {
                                if (response.status === 204) {
                                    toast.success("Lock action successfully", {
                                        position: "bottom-right",
                                        autoClose: 2000
                                    });

                                    const updatedRows = contentRun.map((item) => {
                                        if (item.id === row.id) {
                                            return {
                                                ...item,
                                                lock: !item.lock
                                            };
                                        }
                                        return item;
                                    });

                                    setContentRun(updatedRows); // Update the state

                                } else {
                                    toast.error("Error locking/unlocking the comment", {
                                        position: "bottom-right",
                                        autoClose: 2000
                                    });
                                }
                            });
                        }}
                    ></i>

                </div>
            )
        },
        {
            field: 'timestamp', headerName: 'Date',
            width: "130",
            renderCell: ({ row }) => (<div>{convertDateToString(row.timestamp / 1000)}</div>)
        },
        {
            field: 'duration', headerName: 'Duration',
            width: "70",
            renderCell: ({ row }) => (<div>{convertDurationToString(row.duration / 1000)}</div>)
        },
        {
            field: 'ble_name', headerName: 'Smart Name',
            flex: 1,
        },
        {
            field: 'comment', headerName: 'Comment',
            flex: 1,
            editable: true,
        },
    ];

    if (!isDesktop) {
        columns.pop();
    }

    const processRowUpdate = React.useCallback(
        async (newRow) => {
            console.log("processRowUpdate", newRow);
            const response = await DatabaseService.editPerfsRunComment(newRow.id, newRow.comment);

            if (response.status === 204) {
                toast.success("Comment edited successfully", {
                    position: "bottom-right",
                    autoClose: 2000
                });

                const updatedRows = contentRun.map((item) => {
                    if (item.id === newRow.id) {
                        return {
                            ...item,
                            comment: newRow.comment
                        };
                    }
                    return item;
                });

                setContentRun(updatedRows); // Update the state

            } else {
                toast.error("Error editing the comment", {
                    position: "bottom-right",
                    autoClose: 2000
                });
            }
            return response;
        },
        [contentRun, setContentRun]
    );

    const handleProcessRowUpdateError = React.useCallback((error) => {
        /*
        toast.error("Error editing the comment", {
          position: "bottom-right",
          autoClose: 2000
        });
        */
        console.log("handleProcessRowUpdateError", error);
    }, []);

    const handleFilterModelChange = (filterModel) => {
        setDevicesFilterModel(filterModel);
    };

    const handlePaginationModelChange = (paginationModel) => {
        setDevicesPaginationModel({
            page: paginationModel.page,
            pageSize: paginationModel.pageSize
        });
    }

    const TableToolbar = (props) => {
        return (
            <GridToolbarContainer>
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    };

    return (
        <DataGrid
            key={Math.random()}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            disableVirtualization
            disableRowSelectionOnClick
            disableDensitySelector
            columnHeaderHeight={35}
            rowHeight={30}
            rows={contentRun}
            columns={columns.map((column) => ({ ...column, loadData: loadData }))}
            loadData={loadData}
            removeData={removeData}
            initialState={{
                // ...dataConfig.initialState,
                pagination: { paginationModel: devicesPaginationModel },
                filter: { filterModel: devicesFilterModel },
            }}
            filterModel={devicesFilterModel.filterModel}
            onPaginationModelChange={handlePaginationModelChange}
            onFilterModelChange={handleFilterModelChange}
            slots={{ toolbar: TableToolbar }}
            slotProps={{
                toolbar: {
                    showQuickFilter: true,
                    showExport: false,
                    showFilter: false,
                    // quickFilterProps: { debounceMs: 500 },
                },
            }}
            pageSizeOptions={[isDesktop ? 14 : 10, 20, 25]}
            sx={gridSxStyle}
        >
            <GridToolbarQuickFilter />
        </DataGrid>
    );
} /*, areEqual*/);

export default PerfsGrid;
